import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useHideNavWhile } from '../BottomSheet';

// Icons
const CameraIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const ImageIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const AddCardModal = ({ isOpen, onClose, binders = [], onAddCard }) => {
  useHideNavWhile(isOpen);
  const { theme, isDark } = useTheme();
  const [selectedBinder, setSelectedBinder] = useState(null);
  const [showBinderDropdown, setShowBinderDropdown] = useState(false);

  const handleScanCard = () => {
    // TODO: Implement camera/scan functionality
    console.log('Scan card, binder:', selectedBinder);
    onAddCard?.({ type: 'scan', binder: selectedBinder });
    onClose();
  };

  const handleUploadCard = () => {
    // TODO: Implement file upload
    console.log('Upload card, binder:', selectedBinder);
    onAddCard?.({ type: 'upload', binder: selectedBinder });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ ...styles.backdrop, backgroundColor: theme.overlay }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 1, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 1, y: '100%' }}
            transition={{ type: 'spring', damping: 34, stiffness: 380, mass: 0.9 }}
            style={{ ...styles.modal, backgroundColor: theme.bgSheet }}
          >
            {/* Handle */}
            <div style={styles.handle} />

            {/* Header */}
            <div style={styles.header}>
              <h2 style={{ ...styles.title, color: theme.text }}>Add Card</h2>
              <button style={{ ...styles.closeButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }} onClick={onClose}>
                <CloseIcon />
              </button>
            </div>

            {/* Binder Selection */}
            <div style={styles.section}>
              <label style={{ ...styles.label, color: theme.textMuted }}>Add to Binder (optional)</label>
              <div style={styles.dropdownContainer}>
                <button
                  style={{ ...styles.dropdown, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }}
                  onClick={() => setShowBinderDropdown(!showBinderDropdown)}
                >
                  <span style={styles.dropdownText}>
                    {selectedBinder ? selectedBinder.name : 'No binder (unassigned)'}
                  </span>
                  <ChevronDownIcon />
                </button>

                {showBinderDropdown && (
                  <div style={{ ...styles.dropdownMenu, backgroundColor: theme.bgCard, borderColor: theme.border }}>
                    <button
                      style={{
                        ...styles.dropdownItem,
                        borderColor: theme.border,
                        color: theme.text,
                        ...(selectedBinder === null ? { ...styles.dropdownItemActive, color: theme.accent } : {}),
                      }}
                      onClick={() => {
                        setSelectedBinder(null);
                        setShowBinderDropdown(false);
                      }}
                    >
                      No binder (unassigned)
                    </button>
                    {binders.map((binder) => (
                      <button
                        key={binder.id}
                        style={{
                          ...styles.dropdownItem,
                          borderColor: theme.border,
                          color: theme.text,
                          ...(selectedBinder?.id === binder.id ? { ...styles.dropdownItemActive, color: theme.accent } : {}),
                        }}
                        onClick={() => {
                          setSelectedBinder(binder);
                          setShowBinderDropdown(false);
                        }}
                      >
                        {binder.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={styles.actions}>
              <button style={{ ...styles.actionButton, backgroundColor: theme.bgCard, borderColor: theme.border }} onClick={handleScanCard}>
                <div style={{ ...styles.actionIcon, color: theme.accent }}>
                  <CameraIcon />
                </div>
                <div style={styles.actionContent}>
                  <span style={{ ...styles.actionTitle, color: theme.text }}>Scan Card</span>
                  <span style={{ ...styles.actionDesc, color: theme.textSecondary }}>Use camera to scan</span>
                </div>
              </button>

              <button style={{ ...styles.actionButton, backgroundColor: theme.bgCard, borderColor: theme.border }} onClick={handleUploadCard}>
                <div style={{ ...styles.actionIcon, color: theme.accent }}>
                  <ImageIcon />
                </div>
                <div style={styles.actionContent}>
                  <span style={{ ...styles.actionTitle, color: theme.text }}>Upload Photo</span>
                  <span style={{ ...styles.actionDesc, color: theme.textSecondary }}>Choose from gallery</span>
                </div>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: '80px', // Account for bottom nav
    left: '8px',
    right: '8px',
    backgroundColor: '#151515',
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    maxHeight: 'calc(100vh - 160px)',
    overflow: 'auto',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: {
    width: '36px',
    height: '4px',
    backgroundColor: '#3a3a3a',
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#6a6a6a',
  },
  section: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    color: '#8a8a8a',
    fontSize: '13px',
    marginBottom: '8px',
  },
  dropdownContainer: {
    position: 'relative',
  },
  dropdown: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    color: '#ffffff',
  },
  dropdownText: {
    fontSize: '14px',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    overflow: 'hidden',
    zIndex: 10,
    maxHeight: '200px',
    overflowY: 'auto',
  },
  dropdownItem: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '1px solid #2a2a2a',
    color: '#ffffff',
    fontSize: '14px',
    textAlign: 'left',
    cursor: 'pointer',
  },
  dropdownItemActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  actionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0ea5e9',
  },
  actionContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  actionTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
  },
  actionDesc: {
    color: '#6a6a6a',
    fontSize: '13px',
  },
};

export default AddCardModal;
